// app/features/ai-workflows/services_and_hooks/usePersonalizedUseCasesQueryHook.ts
"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { toast } from "sonner";

import { apiPost } from "@/app/shared/utils/api-helpers";
import { Database } from "@/types/supabase";
import { createClient } from "@/utils/supabase/client";

import {
  KnowledgeBaseTool,
  PersonalizedUseCaseCreationInput,
  PersonalizedUseCaseRestartResponse,
  PersonalizedUseCaseWithToolsApiResponse,
} from "../personalized-use-case.interface";
import { paginatedUseCasesRefetchInterval } from "./paginatedUseCasesPolling";
import {
  fetchPersonalizedUseCases,
  generatePersonalizedUseCase,
  personalizedUseCasesService,
} from "./personalizedUseCasesService";

// ### TRANSITION CONFIGURATION ###
// Toggle between different update strategies during the transition period
const UPDATE_STRATEGY_CONFIG = {
  useWebhookUpdates: true, // Use webhook-based cache invalidation
  useRealtimeSubscriptions: false, // Use Supabase real-time subscriptions (COMMENTED OUT FOR TRANSITION)
  usePollingFallback: false, // Disable polling; rely on webhook invalidation
  enableDebugLogs: false, // Reduce console noise in dashboard
} as const;

// Use Supabase types instead of recreating them
type UseCase = Database["public"]["Tables"]["use_cases"]["Row"];
type PersonalizedUseCase =
  Database["public"]["Tables"]["personalized_use_cases"]["Row"]; // Use specific type
type Organization = Database["public"]["Tables"]["organizations"]["Row"];
type UserOrganization =
  Database["public"]["Tables"]["user_organizations"]["Row"]; // Use specific type
type Feedback = Database["public"]["Tables"]["use_case_feedback"]["Row"]; // Use specific type

// Define a simpler type for the user organization results from the query
// since we're only selecting specific fields
interface UserOrgQueryResult {
  organization_id: string;
  role?: string | null; // Match DB type
  is_primary_organization?: boolean | null; // Match DB type
}

// Create a type that includes our custom fields added to Organization
type EnrichedOrganization = Organization & {
  role?: string | null; // Match DB type
  is_primary?: boolean | null; // Match DB type
};

interface UseCaseDetailApiResponse {
  personalizedUseCase: PersonalizedUseCase | null;
  toolsData: Record<string, KnowledgeBaseTool | null>;
  error?: string; // Optional error message from API
}

// Hook for generating a use case
export const useGenerateUseCaseMutationHook = () => {
  const queryClient = useQueryClient();

  // ### CODE CHANGES START ###
  // This mutation now uses the service function that calls the /api route
  const mutation = useMutation<
    PersonalizedUseCase, // Expect the created DB record on success
    Error, // Type of error
    { inputData: PersonalizedUseCaseCreationInput } // Type of variables passed to mutate/mutateAsync
  >({
    mutationFn: async ({ inputData }) => {
      console.log(
        "[GenerateMutation] mutationFn called with inputData:",
        inputData,
      );
      // ### CODE CHANGES START ###
      // Call the frontend service function which makes the fetch call
      return generatePersonalizedUseCase(inputData);
      // ### CODE CHANGES FINISH ###
    },
    onSuccess: async (createdUseCase) => {
      console.log(
        "[GenerateMutation] onSuccess, invalidating queries, setting data for:",
        createdUseCase.id,
      );

      try {
        // 1. Set the individual use case data in cache for immediate access
        queryClient.setQueryData(
          ["personalizedUseCaseWithTools", createdUseCase.id],
          { personalizedUseCase: createdUseCase, toolsData: {} },
        );

        // 2. Invalidate the user's personalized use cases list
        await queryClient.invalidateQueries({
          queryKey: ["userPersonalizedUseCases", createdUseCase.user_id],
        });

        // 3. 🔥 FIX: Invalidate paginated queries too
        await queryClient.invalidateQueries({
          queryKey: [
            "userPersonalizedUseCasesPaginated",
            createdUseCase.user_id,
          ],
        });

        // 4. Also invalidate the general user use cases query
        await queryClient.invalidateQueries({
          queryKey: ["userPersonalizedUseCases"],
        });

        // 5. Force immediate refetch to show the new use case (paginated)
        await queryClient.refetchQueries({
          queryKey: [
            "userPersonalizedUseCasesPaginated",
            createdUseCase.user_id,
          ],
          type: "active", // Only refetch if query is currently mounted
        });

        // 6. Also refetch non-paginated for backward compatibility
        await queryClient.refetchQueries({
          queryKey: ["userPersonalizedUseCases", createdUseCase.user_id],
          type: "active",
        });

        console.log("[GenerateMutation] Cache updates completed successfully");
      } catch (error) {
        console.error("[GenerateMutation] Error updating cache:", error);
      }
    },
    onError: (error) => {
      console.error("[GenerateMutation] onError:", error.message);
      // Optionally show a toast notification here
    },
  });
  // ### CODE CHANGES END ###

  return mutation;
};

// !v3

// app/features/personalized-use-cases/services_and_hooks/usePersonalizedUseCasesQueryHook.ts

export const usePersonalizedUseCaseByIdQueryHook = (id: string | undefined) => {
  // 1. Ensure queryKey parts are stable
  const queryKey = React.useMemo(
    () => ["personalizedUseCaseWithTools", id] as const,
    [id],
  );
  const isEnabled = !!id && id !== "create";

  // 2. Ensure queryFn is stable
  const fetchData = React.useCallback(async () => {
    console.log(`[ अन्दर fetchData ] ID: ${id} - Fetching now.`); // Log when the actual fetch function is built/called
    if (!isEnabled || !id) {
      // Check isEnabled here too for safety, though useQuery handles it
      // console.warn(`[usePersonalizedUseCaseByIdQueryHook] fetchData called but not enabled or no ID. ID: ${id}, Enabled: ${isEnabled}`);
      return Promise.resolve({
        personalizedUseCase: null,
        toolsData: {},
        error: "Query not enabled or ID missing",
      } as PersonalizedUseCaseWithToolsApiResponse);
    }
    try {
      return await personalizedUseCasesService.fetchPersonalizedUseCaseByIdWithTools(
        id,
      );
    } catch (error) {
      console.error(
        `[usePersonalizedUseCaseByIdQueryHook] Error in fetchPersonalizedUseCaseByIdWithTools for ID ${id}:`,
        error,
      );
      return Promise.resolve({
        // Return a resolved promise with error structure on fetch failure
        personalizedUseCase: null,
        toolsData: {},
        error: (error as Error).message || "Failed to fetch use case",
      } as PersonalizedUseCaseWithToolsApiResponse);
    }
  }, [id, isEnabled]); // isEnabled is added as it's used in the condition now

  const {
    data: responseData,
    isLoading,
    isFetching,
    isError: queryIsError, // Renamed to avoid conflict with apiError
    error: queryError,
    refetch,
  } = useQuery({
    // Removed generic types for brevity if TS can infer them, or add back if needed
    queryKey: queryKey,
    queryFn: fetchData,
    enabled: isEnabled,
    // IMPROVED OPTIONS FOR WEBHOOK-BASED UPDATES:
    staleTime: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? 5 * 60 * 1000
      : 30 * 1000, // Longer cache for webhook updates
    gcTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
    refetchOnWindowFocus: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? false
      : true, // Less aggressive refetching with webhooks
    refetchOnMount: true, // Always fetch fresh data on mount
    refetchOnReconnect: false,
    retry: false, // Disable retries for now
    // 🔥 POLLING: Controlled by config during transition
    refetchInterval: UPDATE_STRATEGY_CONFIG.usePollingFallback ? 3000 : false, // Conditional polling
    // The select function is fine
    select: (
      apiResponseData: PersonalizedUseCaseWithToolsApiResponse,
    ): PersonalizedUseCaseWithToolsApiResponse => {
      return {
        personalizedUseCase: apiResponseData.personalizedUseCase ?? null,
        toolsData: apiResponseData.toolsData ?? {},
        error: apiResponseData.error,
      };
    },
  });

  // ### TRANSITION: REAL-TIME SUBSCRIPTIONS (COMMENTED OUT FOR TESTING) ###
  // const supabase = createClient();
  // React.useEffect(() => {
  //   if (!isEnabled || !id || !UPDATE_STRATEGY_CONFIG.useRealtimeSubscriptions) {
  //     return;
  //   }

  //   if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //     console.log(`[TRANSITION] Setting up real-time subscription for use case: ${id}`);
  //   }

  //   const channel = supabase
  //     .channel(`personalized_use_case_${id}`)
  //     .on(
  //       'postgres_changes',
  //       {
  //         event: 'UPDATE',
  //         schema: 'public',
  //         table: 'personalized_use_cases',
  //         filter: `id=eq.${id}`,
  //       },
  //       (payload) => {
  //         if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //           console.log(`[TRANSITION] Real-time update received for ${id}:`, payload);
  //         }
  //         // Invalidate and refetch the specific use case
  //         queryClient.invalidateQueries({ queryKey: ['personalizedUseCaseWithTools', id] });
  //       }
  //     )
  //     .subscribe();

  //   return () => {
  //     if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //       console.log(`[TRANSITION] Cleaning up real-time subscription for use case: ${id}`);
  //     }
  //     supabase.removeChannel(channel);
  //   };
  // }, [id, isEnabled, queryClient]);

  const apiError = responseData?.error;
  const isError = queryIsError || !!apiError;
  const error = queryError || (apiError ? new Error(apiError) : null);

  return {
    personalizedUseCase: responseData?.personalizedUseCase ?? null,
    toolsData: responseData?.toolsData ?? {},
    isLoading: isLoading || (isEnabled && isFetching && !responseData),
    isError: isError,
    error: error,
    refetch,
  };
};

// Paginated user use cases hook with transition support
export const useUserPersonalizedUseCasesQueryHook = (
  userId: string | undefined,
  paginationOptions?: { limit: number; offset: number },
) => {
  const queryKey = React.useMemo(
    () =>
      paginationOptions
        ? ([
            "userPersonalizedUseCasesPaginated",
            userId,
            paginationOptions.limit,
            paginationOptions.offset,
          ] as const)
        : (["userPersonalizedUseCases", userId] as const),
    [userId, paginationOptions],
  );

  const isEnabled = !!userId;

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }) => {
      if (!userId) return [];

      // For now, just use the basic service function without pagination
      // The webhook will handle updates regardless of pagination
      const allData = await fetchPersonalizedUseCases(userId, signal);

      // Apply pagination client-side if needed
      if (paginationOptions) {
        const { limit, offset } = paginationOptions;
        return allData.slice(offset, offset + limit);
      }

      return allData;
    },
    enabled: isEnabled,
    staleTime: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? 5 * 60 * 1000
      : 30 * 1000, // Longer cache with webhooks
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? false
      : true,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
    // 🔥 POLLING: Controlled by config during transition
    refetchInterval: UPDATE_STRATEGY_CONFIG.usePollingFallback ? 3000 : false,
  });

  // ### TRANSITION: REAL-TIME SUBSCRIPTIONS (COMMENTED OUT FOR TESTING) ###
  // const supabase = createClient();
  // React.useEffect(() => {
  //   if (!isEnabled || !userId || !UPDATE_STRATEGY_CONFIG.useRealtimeSubscriptions) {
  //     return;
  //   }

  //   if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //     console.log(`[TRANSITION] Setting up real-time subscription for user: ${userId}`);
  //   }

  //   const channel = supabase
  //     .channel(`user_personalized_use_cases_${userId}`)
  //     .on(
  //       'postgres_changes',
  //       {
  //         event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
  //         schema: 'public',
  //         table: 'personalized_use_cases',
  //         filter: `user_id=eq.${userId}`,
  //       },
  //       (payload) => {
  //         if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //           console.log(`[TRANSITION] Real-time update for user ${userId}:`, payload);
  //         }

  //         // Invalidate both paginated and non-paginated queries
  //         queryClient.invalidateQueries({
  //           queryKey: ['userPersonalizedUseCases', userId]
  //         });
  //         queryClient.invalidateQueries({
  //           queryKey: ['userPersonalizedUseCasesPaginated', userId]
  //         });

  //         // Also invalidate the specific use case if it exists
  //         if (payload.new?.id) {
  //           queryClient.invalidateQueries({
  //             queryKey: ['personalizedUseCaseWithTools', payload.new.id]
  //           });
  //         }
  //       }
  //     )
  //     .subscribe();

  //   return () => {
  //     if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //       console.log(`[TRANSITION] Cleaning up real-time subscription for user: ${userId}`);
  //     }
  //     supabase.removeChannel(channel);
  //   };
  // }, [userId, isEnabled, queryClient]);

  return {
    personalizedUseCases: data ?? [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

// Hook for submitting feedback
export const useSubmitFeedbackMutationHook = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    // Feedback type needs use_case_id which might be from personalized_use_cases
    mutationFn: async (
      feedbackInput: Omit<Feedback, "id" | "created_at" | "updated_at"> & {
        use_case_id: string;
      },
    ) => {
      const supabase = createClient();

      // Prepare feedback data using service (if complex logic needed)
      // Or directly construct if simple
      const feedbackData = {
        ...feedbackInput,
        // user_id should already be in feedbackInput if needed
      };

      // Store feedback in use_case_feedback
      const { error: feedbackError } = await supabase
        .from("use_case_feedback")
        .insert(feedbackData);

      if (feedbackError) {
        console.error("Error storing feedback:", feedbackError);
        throw new Error(`Failed to submit feedback: ${feedbackError.message}`);
      }

      // If regeneration is requested, update the personalized use case status
      if (feedbackInput.regenerate_requested && feedbackInput.use_case_id) {
        const { error: updateError } = await supabase
          .from("personalized_use_cases") // Update personalized_use_cases table
          .update({
            generation_status: "pending", // Or 'regenerating' if you have that status
            updated_at: new Date().toISOString(),
          })
          .eq("id", feedbackInput.use_case_id); // Match the ID

        if (updateError) {
          console.error(
            "Error updating personalized use case status for regeneration:",
            updateError,
          );
          // Don't necessarily throw, feedback was stored, but log the issue
          console.warn(
            `Feedback stored, but failed to trigger regeneration for ${feedbackInput.use_case_id}: ${updateError.message}`,
          );
        } else {
          console.log(
            `Regeneration triggered for personalized use case ${feedbackInput.use_case_id}`,
          );
        }
      }

      return {
        success: true,
        message: "Feedback submitted successfully",
      };
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific personalized use case query if regeneration was triggered or feedback affects it
      if (variables.use_case_id) {
        queryClient.invalidateQueries({
          queryKey: ["personalizedUseCase", variables.use_case_id],
        });
      }
      // Optionally invalidate list of use cases if feedback impacts lists
      queryClient.invalidateQueries({ queryKey: ["userPersonalizedUseCases"] });
      // 🔥 FIX: Also invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: ["userPersonalizedUseCasesPaginated"],
      });
    },
    onError: (error) => {
      console.error("Feedback submission failed:", error.message);
    },
  });

  return mutation;
};

// !Hook for restarting a failed or incomplete use case generation

/**
 * Frontend service function to call our Next.js API route for restarting a use case.
 * This adheres to the blueprint by calling our own API, not an external one.
 * The backend now auto-determines the optimal restart checkpoint.
 */
async function restartUseCaseViaApi(
  useCaseId: string,
): Promise<PersonalizedUseCaseRestartResponse> {
  console.log(
    `[FE Service] Calling Next.js API to restart use case ${useCaseId} (auto-checkpoint).`,
  );

  const response = await apiPost("/api/personalized-use-cases/restart", {
    useCaseId,
  });

  if (!response.success || !response.data) {
    // The error message from our Next.js API route is in `response.error`
    throw new Error(response.error || "Failed to initiate restart.");
  }

  return response.data;
}

/**
 * Hook for restarting a failed or incomplete use case generation.
 * This now correctly calls our secure Next.js API route.
 */
export const useRestartUseCaseMutationHook = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    PersonalizedUseCaseRestartResponse,
    Error,
    { useCaseId: string }
  >({
    // The mutationFn now calls our secure API proxy (auto-checkpoint)
    mutationFn: ({ useCaseId }) => restartUseCaseViaApi(useCaseId),

    onSuccess: (response, variables) => {
      console.log(
        `[RestartMutation] Successfully initiated restart for use case ${variables.useCaseId}:`,
        response,
      );
      toast.success("Generation restart has been initiated!");

      // Invalidate queries to force the UI to refetch and show the new "pending" status
      queryClient.invalidateQueries({
        queryKey: ["personalizedUseCaseWithTools", variables.useCaseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["userPersonalizedUseCases"],
      });
      // 🔥 FIX: Also invalidate paginated queries
      queryClient.invalidateQueries({
        queryKey: ["userPersonalizedUseCasesPaginated"],
      });
    },
    onError: (error, variables) => {
      console.error(
        `[RestartMutation] Failed to restart use case ${variables.useCaseId}:`,
        error.message,
      );
      toast.error(
        error.message || "Failed to restart generation. Please try again.",
      );
    },
  });

  return mutation;
};

// !Hook for deleting a personalized use case

/**
 * Frontend service function to call our Next.js API route for deleting a use case.
 */
async function deleteUseCaseViaApi(useCaseId: string): Promise<void> {
  console.log(
    `[FE Service] Calling Next.js API to delete use case ${useCaseId}.`,
  );

  const response = await fetch(`/api/personalized-use-cases/${useCaseId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to delete use case.");
  }

  console.log(`[FE Service] Successfully deleted use case ${useCaseId}`);
}

/**
 * Hook for deleting a personalized use case.
 */
export const useDeleteUseCaseMutationHook = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<void, Error, { useCaseId: string }>({
    mutationFn: ({ useCaseId }) => deleteUseCaseViaApi(useCaseId),

    onSuccess: (_, variables) => {
      toast.success("AI Workflow deleted successfully!");

      // Invalidate and refetch ALL related queries immediately
      queryClient.invalidateQueries({
        queryKey: ["userPersonalizedUseCases"],
        exact: false,
        refetchType: "active",
      });

      queryClient.invalidateQueries({
        queryKey: ["userPersonalizedUseCasesPaginated"],
        exact: false,
        refetchType: "active",
      });

      // Remove the specific use case query entirely
      queryClient.removeQueries({
        queryKey: ["personalizedUseCaseWithTools", variables.useCaseId],
      });
    },
    onError: (error, variables) => {
      console.error(
        `[DeleteMutation] Failed to delete use case ${variables.useCaseId}:`,
        error.message,
      );
      toast.error(
        error.message || "Failed to delete AI Workflow. Please try again.",
      );
    },
  });

  return mutation;
};

// ### WEBHOOK-ENABLED PAGINATED HOOK ###
export const useUserPersonalizedUseCasesPaginatedQueryHook = (
  userId: string | undefined,
  paginationOptions: { limit: number; offset: number },
) => {
  const queryClient = useQueryClient();

  // 🔥 FIX: Create a stable query key that handles undefined userId properly
  const queryKey = React.useMemo(() => {
    // Only include userId in key if it exists to prevent null/undefined key issues
    return userId
      ? ([
          "userPersonalizedUseCasesPaginated",
          userId,
          paginationOptions,
        ] as const)
      : ([
          "userPersonalizedUseCasesPaginated",
          "no-user",
          paginationOptions,
        ] as const);
  }, [userId, paginationOptions]);

  const { data, isLoading, isError, error, refetch } = useQuery<
    { data: PersonalizedUseCase[]; totalCount: number },
    Error
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!userId) {
        return { data: [], totalCount: 0 };
      }

      // For webhook approach, we'll fetch all data and paginate client-side
      // In a production app, you'd want server-side pagination, but for transition this works
      const allData = await fetchPersonalizedUseCases(userId, signal);
      const { limit, offset } = paginationOptions;

      return {
        data: allData.slice(offset, offset + limit),
        totalCount: allData.length,
      };
    },
    enabled: !!userId, // 🔥 FIX: Keep this but now with stable query key
    staleTime: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? 5 * 60 * 1000
      : 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? false
      : true,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchInterval: (query) =>
      paginatedUseCasesRefetchInterval(
        UPDATE_STRATEGY_CONFIG.usePollingFallback,
        query.state.data?.data,
      ),
    // 🔥 FIX: Add key to force new query when userId changes from undefined to defined
    queryKeyHashFn: (queryKey) => JSON.stringify(queryKey),
    retry: (failureCount, error) => {
      const errorMessage = error.message?.toLowerCase() || "";

      if (
        errorMessage.includes("unauthorized") ||
        errorMessage.includes("403") ||
        errorMessage.includes("forbidden")
      ) {
        return false;
      }

      return failureCount < 1;
    },
    retryDelay: 3000,
    networkMode: "online",
  });

  // ### TRANSITION: REAL-TIME SUBSCRIPTIONS (COMMENTED OUT FOR TESTING) ###
  // const supabase = createClient();
  // React.useEffect(() => {
  //   if (!userId || !UPDATE_STRATEGY_CONFIG.useRealtimeSubscriptions) {
  //     return;
  //   }

  //   if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //     console.log(`[TRANSITION] Setting up paginated real-time subscription for user: ${userId}`);
  //   }

  //   const channel = supabase
  //     .channel(`user_personalized_use_cases_paginated_${userId}`)
  //     .on(
  //       'postgres_changes',
  //       {
  //         event: '*',
  //         schema: 'public',
  //         table: 'personalized_use_cases',
  //         filter: `user_id=eq.${userId}`,
  //       },
  //       (payload) => {
  //         if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //           console.log(`[TRANSITION] Paginated real-time update for user ${userId}:`, payload);
  //         }
  //         queryClient.invalidateQueries({ queryKey: ['userPersonalizedUseCasesPaginated', userId] });
  //       }
  //     )
  //     .subscribe();

  //   return () => {
  //     if (UPDATE_STRATEGY_CONFIG.enableDebugLogs) {
  //       console.log(`[TRANSITION] Cleaning up paginated real-time subscription for user: ${userId}`);
  //     }
  //     supabase.removeChannel(channel);
  //   };
  // }, [userId, queryClient]);

  return {
    data: data?.data ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isError,
    error,
    refetch,
  };
};

// v2

// v1
// export const useRestartUseCaseMutationHook = () => {
//   const queryClient = useQueryClient();

//   const mutation = useMutation<
//     PersonalizedUseCaseRestartResponse,
//     Error,
//     { useCaseId: string; checkpoint: RestartCheckpoint }
//   >({
//     mutationFn: async ({ useCaseId, checkpoint }) => {
//       console.log(`[RestartMutation] Restarting use case ${useCaseId} from checkpoint: ${checkpoint}`);
//       return restartUseCaseGeneration(useCaseId, checkpoint);
//     },
//     onSuccess: (response, variables) => {
//       console.log(`[RestartMutation] Successfully restarted use case ${variables.useCaseId}:`, response);

//       // Invalidate the specific use case query to refetch its status
//       queryClient.invalidateQueries({
//         queryKey: ['personalizedUseCaseWithTools', variables.useCaseId]
//       });

//       // Also invalidate the user's use cases list
//       queryClient.invalidateQueries({
//         queryKey: ['userPersonalizedUseCases']
//       });
//     },
//     onError: (error, variables) => {
//       console.error(`[RestartMutation] Failed to restart use case ${variables.useCaseId}:`, error.message);
//       // Handle user-facing error notification (e.g., toast)
//     }
//   });

//   // Optional: Global state hook for loading/error handling
//   useDataFetchingHook({
//     isLoading: mutation.isPending,
//     isError: mutation.isError,
//     error: mutation.error,
//     errorMessage: 'Failed to restart use case generation',
//     loadingName: 'restartUseCase',
//   });

//   return mutation;
// };

const personalizedUseCasesQueryKeys = {
  all: ["personalizedUseCases"] as const,
  allForUser: (userId: string | undefined) =>
    ["userPersonalizedUseCases", userId] as const,
  detail: (id: string) => ["personalizedUseCases", "detail", id] as const,
};

export const useUserPersonalizedUseCasesListAndCreate = (
  userId: string | undefined,
) => {
  // Renamed for clarity
  const queryClient = useQueryClient();

  // Query to fetch all personalized use cases for the user
  const {
    data: personalizedUseCases = [],
    isLoading: isLoadingUseCases,
    isError: isErrorUseCases,
    error: errorUseCases,
    refetch: refetchUseCases,
  } = useQuery<PersonalizedUseCase[], Error>({
    // Correctly typed as array
    queryKey: personalizedUseCasesQueryKeys.allForUser(userId),
    queryFn: () => {
      if (!userId) return Promise.resolve([]); // Return empty array if no userId
      return fetchPersonalizedUseCases(userId); // This FE service calls GET /api/personalized-use-cases
      // which should filter by logged-in user via server-side auth context
    },
    enabled: !!userId, // Only run if userId is available
    staleTime: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? 5 * 60 * 1000
      : 60 * 1000, // Longer cache for webhook updates
    refetchOnMount: true, // Ensure fresh data on mount
    refetchOnWindowFocus: UPDATE_STRATEGY_CONFIG.useWebhookUpdates
      ? false
      : true,
    refetchInterval: UPDATE_STRATEGY_CONFIG.usePollingFallback ? 3000 : false, // Conditional polling
  });

  // Mutation to generate a new personalized use case
  const {
    mutateAsync: generateUseCase, // Using mutateAsync to easily await it in the main hook
    isPending: isGenerating,
    isSuccess: isGenerationSuccess,
    isError: isGenerationError,
    error: generationError,
    reset: resetGenerationMutation, // Renamed to avoid conflict if you use 'reset' from RHF
  } = useMutation<PersonalizedUseCase, Error, PersonalizedUseCaseCreationInput>(
    {
      // Input is PersonalizedUseCaseCreationInput
      mutationFn: (input: PersonalizedUseCaseCreationInput) => {
        console.log(
          "[useUserPersonalizedUseCasesListAndCreate - Mutation] Calling generatePersonalizedUseCase service with:",
          input,
        );
        return generatePersonalizedUseCase(input); // This FE service calls POST /api/personalized-use-cases
      },
      onSuccess: (newlyCreatedUseCase) => {
        console.log(
          "[useUserPersonalizedUseCasesListAndCreate - Mutation] onSuccess, new use case:",
          newlyCreatedUseCase,
        );

        if (userId) {
          // 🔥 IMMEDIATE: Optimistically add the new use case to the list cache
          const currentData = queryClient.getQueryData<PersonalizedUseCase[]>(
            personalizedUseCasesQueryKeys.allForUser(userId),
          );

          if (currentData) {
            // Add the new use case to the beginning of the list
            queryClient.setQueryData(
              personalizedUseCasesQueryKeys.allForUser(userId),
              [newlyCreatedUseCase, ...currentData],
            );
          }

          // Invalidate and refetch to get the authoritative data
          queryClient.invalidateQueries({
            queryKey: personalizedUseCasesQueryKeys.allForUser(userId),
          });
          // Force immediate refetch to ensure UI updates right away
          queryClient.refetchQueries({
            queryKey: personalizedUseCasesQueryKeys.allForUser(userId),
          });
        }

        // Also invalidate general queries without user_id
        queryClient.invalidateQueries({
          queryKey: ["userPersonalizedUseCases"],
        });

        // Optimistically update the cache for the detail view if needed immediately
        queryClient.setQueryData(
          ["personalizedUseCaseWithTools", newlyCreatedUseCase.id],
          { personalizedUseCase: newlyCreatedUseCase, toolsData: {} },
        );
      },
      onError: (error) => {
        console.error(
          "[useUserPersonalizedUseCasesListAndCreate - Mutation] Error generating use case:",
          error.message,
        );
        // Handle user-facing error notification (e.g., toast)
      },
    },
  );

  return {
    // List Query
    personalizedUseCases, // This is PersonalizedUseCase[]
    isLoadingUseCases,
    isErrorUseCases,
    errorUseCases,
    refetchUseCases,

    // Generation Mutation
    generateUseCase, // This is mutateAsync from the useMutation hook
    isGenerating,
    isGenerationSuccess,
    isGenerationError,
    generationError,
    resetGenerationMutation,
  };
};
